import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import InventoryRoundedIcon from "@mui/icons-material/InventoryRounded";
import PercentRoundedIcon from "@mui/icons-material/PercentRounded";
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SpaceDashboardRoundedIcon from "@mui/icons-material/SpaceDashboardRounded";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import ModuleDisabledState from "../../../components/dashboard/ModuleDisabledState";
import { projectsApi } from "../../../api/projects";
import { erpApi } from "../../../api/erp";
import { useProjects } from "../../../context/ProjectsContext";
import { isProjectModuleEnabled } from "../../../utils/projectModules";
import { ERP_BRAND } from "../../../utils/erp";
import {
  hasAnyProjectPermission,
  hasProjectPermission,
  normalizeProjectAccess,
  projectRoleLabel,
} from "../../../utils/projectAccess";

const PRIMARY_TABS = [
  {
    key: "overview",
    label: "Resumen contable",
    path: "",
    icon: <SpaceDashboardRoundedIcon />,
    permissions: ["erp.overview.read"],
  },
  {
    key: "items",
    label: "Ítems",
    path: "items",
    icon: <CategoryRoundedIcon />,
    permissions: ["items.read"],
  },
  {
    key: "movements",
    label: "Operaciones",
    path: "movimientos",
    icon: <ReceiptLongRoundedIcon />,
    permissions: ["erp.movements.read"],
  },
  {
    key: "cash",
    label: "Caja",
    path: "caja",
    icon: <PointOfSaleRoundedIcon />,
    permissions: ["cash.registers.read", "cash.sessions.read", "cash.movements.read"],
  },
  {
    key: "stock",
    label: "Inventario",
    path: "stock",
    icon: <InventoryRoundedIcon />,
    permissions: ["erp.stock.read"],
  },
  {
    key: "accounting",
    label: "Contabilidad",
    path: "ledger",
    icon: <AccountBalanceRoundedIcon />,
    permissions: ["erp.ledger.read", "erp.transactions.read", "erp.tax_profiles.read"],
  },
];

const ACCOUNTING_TABS = [
  {
    key: "ledger",
    label: "Libro contable",
    path: "ledger",
    icon: <AccountBalanceRoundedIcon />,
    permissions: ["erp.ledger.read"],
  },
  {
    key: "transactions",
    label: "Cobros y pagos",
    path: "transacciones",
    icon: <SwapHorizRoundedIcon />,
    permissions: ["erp.transactions.read"],
  },
  {
    key: "accounts",
    label: "Plan de cuentas",
    path: "cuentas",
    icon: <AccountTreeRoundedIcon />,
    permissions: ["erp.ledger.read"],
  },
  {
    key: "taxes",
    label: "Impuestos",
    path: "impuestos",
    icon: <PercentRoundedIcon />,
    permissions: ["erp.tax_profiles.read"],
  },
];

function canAccessTab(access, tab) {
  return hasAnyProjectPermission(access, tab.permissions || []);
}

function pathSegment(pathname) {
  return String(pathname || "").split("/").filter(Boolean).pop() || "";
}

function primaryValue(pathname) {
  const segment = pathSegment(pathname);
  if (["ledger", "transacciones", "cuentas", "impuestos"].includes(segment)) return "accounting";
  if (segment === "items") return "items";
  if (segment === "movimientos") return "movements";
  if (segment === "caja") return "cash";
  if (segment === "stock") return "stock";
  return "overview";
}

function accountingValue(pathname) {
  const segment = pathSegment(pathname);
  return ACCOUNTING_TABS.some((tab) => tab.path === segment) ? segment : "ledger";
}

export default function ProjectErpLayout() {
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { projects, activeProject, setActiveProject, loadProjects } = useProjects();
  const [project, setProject] = React.useState(null);
  const [access, setAccess] = React.useState(normalizeProjectAccess());
  const [bootstrap, setBootstrap] = React.useState({
    accounts: [],
    registers: [],
    taxProfiles: [],
    openSessions: [],
  });
  const [loading, setLoading] = React.useState(true);
  const [bootstrapping, setBootstrapping] = React.useState(false);
  const [error, setError] = React.useState("");

  const contextProject = React.useMemo(
    () =>
      projects.find(
        (item) => item.slug === projectId || item.id === projectId || item._id === projectId,
      ) || activeProject,
    [projects, projectId, activeProject],
  );
  const resolvedProject = project || contextProject;
  const superAdmin = access.role === "superadmin";
  const enabled = superAdmin || isProjectModuleEnabled(resolvedProject, "system");
  const visibleTabs = React.useMemo(
    () => PRIMARY_TABS.filter((tab) => canAccessTab(access, tab)),
    [access],
  );
  const visibleAccountingTabs = React.useMemo(
    () => ACCOUNTING_TABS.filter((tab) => canAccessTab(access, tab)),
    [access],
  );
  const canBootstrap = hasAnyProjectPermission(access, [
    "erp.movements.read",
    "erp.ledger.read",
    "cash.registers.read",
    "erp.stock.read",
  ]);

  React.useEffect(() => {
    if (!projects.length) loadProjects();
  }, [projects.length, loadProjects]);

  React.useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError("");
    projectsApi
      .get(projectId)
      .then((data) => {
        if (!mounted) return;
        setProject(data.project || null);
        setAccess(normalizeProjectAccess(data.memberAccess || data.member || {}));
        if (data.project) setActiveProject(data.project);
      })
      .catch((err) => {
        if (mounted) setError(err.message || "No se pudo cargar el proyecto");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [projectId, setActiveProject]);

  const refreshBootstrap = React.useCallback(async () => {
    if (!projectId || !enabled || !canBootstrap) return null;
    try {
      setBootstrapping(true);
      const data = await erpApi.bootstrap(projectId);
      if (data.access) setAccess(normalizeProjectAccess(data.access));
      const next = {
        accounts: data.accounts || [],
        registers: data.registers || [],
        taxProfiles: data.taxProfiles || [],
        openSessions: data.openSessions || [],
      };
      setBootstrap(next);
      return next;
    } catch (err) {
      setError(err.message || "No se pudo preparar la estructura del sistema");
      return null;
    } finally {
      setBootstrapping(false);
    }
  }, [projectId, enabled, canBootstrap]);

  React.useEffect(() => {
    if (!loading && enabled && canBootstrap) refreshBootstrap();
  }, [loading, enabled, canBootstrap, refreshBootstrap]);

  const base = `/dashboard/proyectos/${projectId}/sistema`;
  const currentTab = primaryValue(location.pathname);
  const currentAccountingTab = accountingValue(location.pathname);

  React.useEffect(() => {
    if (loading || !enabled || !visibleTabs.length) return;
    if (!visibleTabs.some((tab) => tab.key === currentTab)) {
      const first = visibleTabs[0];
      navigate(first.path ? `${base}/${first.path}` : base, { replace: true });
    }
  }, [loading, enabled, visibleTabs, currentTab, navigate, base]);

  React.useEffect(() => {
    if (loading || currentTab !== "accounting" || !visibleAccountingTabs.length) return;
    if (!visibleAccountingTabs.some((tab) => tab.path === currentAccountingTab)) {
      navigate(`${base}/${visibleAccountingTabs[0].path}`, { replace: true });
    }
  }, [base, currentAccountingTab, currentTab, loading, navigate, visibleAccountingTabs]);

  if (loading) return <Alert severity="info">Cargando gestión del negocio...</Alert>;

  if (!enabled) {
    return (
      <ModuleDisabledState
        projectId={projectId}
        title="Gestión del negocio"
        description="Este módulo incorpora ítems operativos, caja, inventario y contabilidad."
      />
    );
  }

  if (!visibleTabs.length) {
    return (
      <Alert severity="warning">
        Tu acceso está activo, pero no tenés permisos para consultar la gestión del negocio. Pedile al titular que revise tu rol.
      </Alert>
    );
  }

  return (
    <Stack spacing={2.5}>
      {error ? (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}

      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.2} flexWrap="wrap" useFlexGap>
            <Typography variant="h3" sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>
              Gestión del negocio
            </Typography>
            <Chip label={projectRoleLabel(access.role)} color="success" size="small" sx={{ fontWeight: 850 }} />
            {access.scopes.ownOperationsOnly ? (
              <Chip label="Solo operaciones propias" size="small" variant="outlined" />
            ) : null}
            {access.blindCashClosing ? (
              <Chip label="Arqueo sin saldo previo" size="small" variant="outlined" color="warning" />
            ) : null}
          </Stack>
          <Typography sx={{ color: ERP_BRAND.muted, mt: 0.5 }}>
            {resolvedProject?.name || projectId} · ventas, compras, caja, inventario y contabilidad
          </Typography>
        </Box>
        {canBootstrap ? (
          <Button
            variant="outlined"
            startIcon={<RefreshRoundedIcon />}
            disabled={bootstrapping}
            onClick={refreshBootstrap}
            sx={{
              alignSelf: "flex-start",
              borderRadius: 2.5,
              textTransform: "none",
              fontWeight: 850,
            }}
          >
            Actualizar datos
          </Button>
        ) : null}
      </Stack>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${ERP_BRAND.border}`,
          overflowX: "auto",
        }}
      >
        <Tabs
          value={currentTab}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            px: 1,
            minHeight: 58,
            "& .MuiTab-root": {
              minHeight: 58,
              textTransform: "none",
              fontWeight: 850,
            },
          }}
        >
          {visibleTabs.map((tab) => (
            <Tab
              key={tab.key}
              value={tab.key}
              icon={tab.icon}
              iconPosition="start"
              label={tab.label}
              onClick={() => navigate(tab.path ? `${base}/${tab.path}` : base)}
            />
          ))}
        </Tabs>
      </Paper>

      {currentTab === "accounting" && visibleAccountingTabs.length ? (
        <Tabs
          value={currentAccountingTab}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 42,
            "& .MuiTab-root": {
              minHeight: 42,
              textTransform: "none",
              fontWeight: 800,
            },
          }}
        >
          {visibleAccountingTabs.map((tab) => (
            <Tab
              key={tab.key}
              value={tab.path}
              icon={tab.icon}
              iconPosition="start"
              label={tab.label}
              onClick={() => navigate(`${base}/${tab.path}`)}
            />
          ))}
        </Tabs>
      ) : null}

      <Outlet
        context={{
          project: resolvedProject,
          access,
          bootstrap,
          refreshBootstrap,
          setGlobalError: setError,
          can: (permission) => hasProjectPermission(access, permission),
        }}
      />
    </Stack>
  );
}
